import {BACKEND_URL} from '../config';
import DetailedError from './DetailedError';
import {isAbortControllerAvailable, isAbortError, retryOnError} from '../utils/utils';
import {getGlobalAuthService} from '../auth/AuthContextProvider';

const URL = BACKEND_URL;

export enum AbortCategory {
    SEARCH,
    PREVIEW_FILE,
    PREVIEW_HIGHLIGHT,
    DOWNLOAD,
    SUGGEST,
    UPDATE,
    UI_CONFIG,
    FACETS,
    DETAILS,
    TRANSLATIONS,
    SAVED_SEARCH,
    MATA_INFO_OPTIONS,
    AI_GENERATE,
    AI_SUGGESTION,
    CLOSE_PIT,
}

const abortControllerMap = new Map<AbortCategory, AbortController>();

export const postJsonWithBody = <T>(
    location: string,
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<T> => {
    return retryOnError(() => fetchWithBody(location, 'POST', body, abortCategory, cache), [504], 3);
};

export const putJsonWithBody = <T>(
    location: string,
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<T> => {
    return retryOnError(() => fetchWithBody(location, 'PUT', body, abortCategory, cache), [504], 3);
};

export const fetchFile = async (location: string, body: any, abortCategory?: AbortCategory): Promise<Blob> => {
    const options = {
        method: 'POST',
        headers: await getHeadersForJsonType(false),
        body,
        credentials: 'include' as RequestCredentials,
    };
    return retryOnError(() => doFetchFile<Blob>(URL + location, options, abortCategory), [504], 3,);
};

export const postJsonWithBodyForStream = (
    location: string,
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<Response> => {
    return streamWithBody(location, 'POST', body, abortCategory, cache);
};

export const fetchWithBody = async <T>(
    location: string,
    method = 'GET',
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<T> => {
    const cacheToUse: RequestCache = cache ? cache : 'default';
    const options = {
        method: method,
        headers: await getHeadersForJsonType(false),
        cache: cacheToUse,
        body,
        credentials: 'include' as RequestCredentials,
    };
    return doFetchContent(URL + location, options, abortCategory);
};

export const fetchJson = async <T>(
    location: string,
    method = 'GET',
    abortCategory?: AbortCategory,
    noAuth?: boolean,
): Promise<T> => {
    const options = {
        method: method,
        headers: await getHeadersForJsonType(noAuth),
        credentials: 'include' as RequestCredentials,
    };
    return doFetchContent(URL + location, options, abortCategory);
};

export const deleteById = async <T>(
    location: string,
    id: number | string,
    abortCategory?: AbortCategory,
): Promise<T> => {
    const options = {
        method: 'DELETE',
        headers: await getHeaders(),
        credentials: 'include' as RequestCredentials,
    };
    return doFetchContent(URL + location + '/' + id, options, abortCategory);
};


export const streamWithBody = async (
    location: string,
    method = 'GET',
    body: any,
    abortCategory?: AbortCategory,
    cache?: RequestCache,
): Promise<Response> => {
    const cacheToUse: RequestCache = cache ? cache : 'default';
    const options = {
        method: method,
        headers: {
            ...(await getHeadersForJsonType(false)),
            Accept: 'text/event-stream',
        },
        cache: cacheToUse,
        body,
        credentials: 'include' as RequestCredentials,
    };
    return doStream(URL + location, options, abortCategory);
};

export async function getHeaders(noAuth?: boolean) {
    const headers: Record<string, string> = {};

    if (!noAuth) {
        const globalAuthService = await getGlobalAuthService();
        if (!globalAuthService.isEnabled()) {
            headers['X-User-Id'] = globalAuthService.getUserId();
        }
    }

    const csrfToken = getCsrfToken();
    if (csrfToken) {
        headers['X-XSRF-TOKEN'] = csrfToken;
    }

    return headers;
}

export function getCsrfToken(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
}

const getHeadersForJsonType = async (noAuth?: boolean) => {
    const headers = await getHeaders(noAuth);
    headers['Content-Type'] = 'application/json';
    return headers;
};


function doStream(url: RequestInfo, options: RequestInit, abortCategory?: AbortCategory): Promise<Response> {
    options = handleAbortFetch(options, abortCategory);
    return fetch(url, options).then(
        (r) => r,
        (e) => handleNonServerErrors(e),
    );
}

/**
 * Performs the actual fetch request.
 * Returns either
 * - a Promise of given generic type
 * - throws a DetailedError, if the response is not ok or
 * - returns undefined, if the request was aborted
 */
function doFetchFile<T>(url: RequestInfo, options: RequestInit, abortCategory?: AbortCategory): Promise<T> {
    options = handleAbortFetch(options, abortCategory);
    return fetch(url, options).then(
        (r) => extractBlobFromResponse(r),
        (e) => handleNonServerErrors(e),
    );
}

/**
 * Performs the actual fetch request.
 * Returns either
 * - a Promise of given generic type
 * - throws a DetailedError, if the response is not ok or
 * - returns undefined, if the request was aborted
 */
function doFetchContent<T>(url: RequestInfo, options: RequestInit, abortCategory?: AbortCategory): Promise<T> {
    options = handleAbortFetch(options, abortCategory);
    return fetch(url, options).then(
        (r) => extractContentFromResponse(r),
        (e) => handleNonServerErrors(e),
    );
}

/**
 * Extracts the Blob of a given Response.
 *
 */
async function extractBlobFromResponse<T>(response: Response): Promise<T> {
    return getResponseOrThrowError(response, response.blob());
}

/**
 * Extracts the content of a given Response.
 *
 */
async function extractContentFromResponse<T>(response: Response): Promise<T> {
    const hasJsonBody = response.headers.get('content-type')?.startsWith('application/json');
    const extractedContent = (hasJsonBody ? response.json() : response.text()).catch(() => undefined);
    return getResponseOrThrowError(response, extractedContent);
}

/**
 * If the response is ok, the method returns
 * - either a json, if there is a json response,
 * - a text if there is a text response or
 * - undefined if there is neither of those
 *
 * If the response is not ok, the method throws an according DetailedError
 *
 * @param response a response maybe containing content of the given generic response type
 * @param extractedContent extracted content of a Response
 * @return a Promise of the given generic response type
 * @throws DetailedError if the response is not ok
 */
async function getResponseOrThrowError(response: Response, extractedContent: any) {
    if (response.ok) {
        return extractedContent;
    }
    switch (response.status) {
        case 401: {
            const authSvc = await getGlobalAuthService();
            if (authSvc.isEnabled()) {
                authSvc.login();
                return undefined as any;
            }
            throw new DetailedError({
                errorType: response.status,
                errorMessage: 'search.error.credentials',
                source: 'fetcher@fetchHandler',
            });
        }
        case 403:
            throw new DetailedError({
                errorType: response.status,
                errorMessage: 'search.error.forbidden',
            });
        case 404:
            throw new DetailedError({
                errorType: response.status,
                errorMessage: 'search.error.fileNotFound',
            });
        default:
            throw new DetailedError({
                errorType: response.status,
                errorMessage: 'search.error.general',
            });
    }
}

//TODO this is not used so far. Maybe source out logic that is now in the utils.ts fetchStreamedAiResponse to here
async function extractContentFromStream<T>(response: Response): Promise<T> {
    const reader = response.body?.getReader();

    // Check if the `reader` is a valid read-only stream and process accordingly
    if (reader) {
        let results = '';
        let isCompleted = false;
        while (!isCompleted) {
            const {done, value} = await reader.read();
            isCompleted = done;
            if (done) {
                continue;
            }
            results += new TextDecoder('utf-8').decode(value, {stream: true});
        }

        return JSON.parse(results);
    }

    throw new DetailedError({
        errorType: response.status,
        errorMessage: response.statusText,
    });
}

/**
 * This function handles all errors that are not caused by the services.
 * Examples: Abort errors, service not reachable, etc.
 *
 * If the given error is an abort error, then the function does not throw an error but returns silently with undefined.
 * In all other cases, a DetailedError is thrown.
 *
 * @param e either an abort error or any other error object
 * @return undefined if the given error is an abort error
 * @throws DetailedError if the given error is not an abort error
 */
function handleNonServerErrors<T>(e: Error): Promise<T> {
    if (isAbortError(e)) {
        // @ts-ignore todo: find a better solution than ignoring the empty response
        return;
    }
    throw new DetailedError({
        errorType: 0,
        errorMessage: `${e.name}: ${e.message}`,
        errorData: e.stack,
    });
}

/**
 * Handles aborting a fetch call, if a call is already pending with the same {@link AbortCategory}.
 */
function handleAbortFetch(options: RequestInit, abortCategory: AbortCategory | undefined): RequestInit {
    if (abortCategory === undefined || !isAbortControllerAvailable()) {
        return options;
    }

    checkFetchesToAbort(abortCategory);
    return addAbortController(options, abortCategory);
}

/**
 * Adds a new {@link AbortController} if an {@link AbortCategory} is defined and adds it to the options.
 */
function addAbortController(options: RequestInit, abortCategory: AbortCategory): RequestInit {
    const controller = new AbortController();
    abortControllerMap.set(abortCategory, controller);
    options.signal = controller.signal;
    return options;
}

/**
 * Abort previously made and still in-progress fetches of the same abort category.
 * Additionally, when a search fetch is executed, also abort previous suggest fetches.
 * @param abortCategory abort category of the fetch to execute.
 */
function checkFetchesToAbort(abortCategory: AbortCategory) {
    if (abortCategory === AbortCategory.SEARCH) {
        checkFetchesToAbortInternal(AbortCategory.SUGGEST);
    }
    checkFetchesToAbortInternal(abortCategory);
}

/**
 * Check if a fetch has previously been made. If it is stil in progress and has an {@link AbortCategory},
 * it will be aborted.
 */
function checkFetchesToAbortInternal(abortCategory: AbortCategory) {
    const abortController = abortControllerMap.get(abortCategory);
    if (abortController) {
        abortController.abort();
        abortControllerMap.delete(abortCategory);
    }
}
